import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './HomeComercial.css';
import NumeroSuerte from './NumeroSuerte';
import { useExtractos, ticker as tickerDeExtractos } from './useExtractos';
import TableroExtractos from './TableroExtractos';
import ExtractosJuegos from './ExtractosJuegos';
import CarruselJuegos from './CarruselJuegos';
import LeyendaJuegoResponsable from './LeyendaJuegoResponsable';

// Home comercial "INPROJUY Juega" — port React del mockup dirigido por Fable.
// Animación "cinematográfica pero rápida": entrada del hero, count-up del pozo,
// bolillas en cascada, reveals al scroll. Sin librerías (CSS + IntersectionObserver).
const PUB = process.env.PUBLIC_URL;
const LOGO_BLANCO = `${PUB}/img/logo_inpro_white.png`;

// Solo destinos que EXISTEN. "Cómo se juega" y "Dónde jugar" apuntaban a ids que no
// eran secciones: estaban colgados de links de texto adentro de otras secciones, así
// que el menú prometía cinco lugares y había tres.
// Hasta que existan esas dos secciones, "Cómo se juega" lleva a la grilla de juegos,
// donde cada tarjeta tiene su propio "Cómo se juega".
const NAV_LINKS = [
  { label: 'Inicio', href: '#inicio' },
  { label: 'Juegos', href: '#juegos' },
  { label: 'Cómo se juega', href: '#juegos' },
  { label: 'Resultados', href: '#resultados' },
];

// Logos oficiales scrapeados de loteríalajujeña.com.ar → public/img/juegos/
//
// P5 · Jerarquía y textos propios.
//
// `dato` es la línea que reemplaza a los ocho "Cómo se juega" idénticos que
// había acá. Ocho enlaces que empiezan con las mismas palabras se leen como
// uno solo (patrón de *bypassing*): la información diferenciadora va primero.
// Cada línea sale de lo que ya sabíamos del juego —la frecuencia, las
// modalidades de las fichas, la mecánica—; ningún monto, ningún precio y
// ninguna fecha inventada (N1).
//
// `destacada` es la Tómbola y solamente la Tómbola: es el juego propio de la
// provincia y el de mayor frecuencia (cuatro sorteos por día contra uno o dos
// por semana del resto). Es una decisión editorial fija, no la salida de un
// algoritmo que destaque al del pozo más alto.
//
// `cta` solo lo tiene la Tómbola, y apunta a `#resultados`, que EXISTE en esta
// página. Las otras siete no llevan enlace: el destino natural de todas es el
// simulador de jugada (P2), que todavía no está construido. Un enlace que
// apunta al vacío es peor que ningún enlace — el `#como-se-juega` anterior ni
// siquiera scrolleaba, porque ese id no existe en el documento.
const JUEGOS = [
  {
    clase: 'hc-g-tombola', freq: 'Todos los días', nombre: 'Tómbola Jujeña',
    logo: `${PUB}/img/juegos/tombola.png`,
    destacada: true,
    sub: 'La quiniela de Jujuy: la de casa, la que más veces sale.',
    dato: 'Cuatro sorteos por día',
    chips: ['La Previa', 'Matutino', 'La Primera', 'Nocturno'],
    cta: { label: 'Ver los extractos de hoy', href: '#resultados' },
  },
  {
    clase: 'hc-g-loto', freq: 'Miércoles y sábados', nombre: 'Loto Plus',
    logo: `${PUB}/img/juegos/lotoplus.png`,
    dato: 'El pozo más grande del país',
    chips: ['Tradicional', 'Match', 'Desquite', 'Sale o Sale'], delay: 'dly-1',
  },
  {
    clase: 'hc-g-quini', freq: 'Miércoles y domingos', nombre: 'Quini 6',
    logo: `${PUB}/img/juegos/quini6.png`,
    dato: 'Cuatro modalidades por jugada',
    chips: ['Tradicional', 'Segunda', 'Revancha', 'Siempre Sale'], delay: 'dly-2',
  },
  {
    clase: 'hc-g-brinco', freq: 'Domingos', nombre: 'Brinco',
    logo: `${PUB}/img/juegos/brinco.png`,
    // Seis, no cinco. El extracto oficial de Lotería de Santa Fe del 16/08/2026
    // (concurso 1367) publica "09 16 19 20 36 39" en Extracciones y otros seis en
    // Brinco Junior. La ficha decía "5 números" desde antes de este trabajo: era
    // una mecánica equivocada publicada en la home de un organismo de juego (N1).
    dato: 'Seis números, sorteo del domingo',
    chips: ['6 números', 'Sorteo semanal'], delay: 'dly-3',
  },
  {
    clase: 'hc-g-loto5', freq: 'Viernes', nombre: 'Loto 5 Plus',
    logo: `${PUB}/img/juegos/loto5plus.png`,
    dato: 'Cinco números, un solo viernes',
    chips: ['5 aciertos'],
  },
  {
    clase: 'hc-g-pozo', freq: 'Todas las noches', nombre: 'Pozo de la Quiniela',
    logo: `${PUB}/img/juegos/pozo.png`,
    dato: 'Con Super Bola, todas las noches',
    chips: ['Super Bola'], delay: 'dly-1',
  },
  {
    clase: 'hc-g-telekino', freq: 'Domingos', nombre: 'Telekino',
    logo: `${PUB}/img/juegos/telekino.png`,
    dato: 'Se juega con cartón y se sortea por TV',
    chips: ['Cartón', 'Sorteo TV'], delay: 'dly-2',
  },
  {
    clase: 'hc-g-loteria', freq: 'Sorteo semanal', nombre: 'Lotería',
    logo: `${PUB}/img/juegos/loteria.png`,
    dato: 'Billete numerado, un premio mayor',
    chips: ['Billete', 'Premio mayor'], delay: 'dly-3',
  },
];

const BOLAS_FLOTANTES = [
  { n: '07', top: '12%', left: '4%', dur: '11s', delay: '0s', size: 54 },
  { n: '23', top: '68%', left: '9%', dur: '14s', delay: '-4s', size: 40 },
  { n: '48', top: '26%', left: '46%', dur: '12s', delay: '-7s', size: 34 },
  { n: '15', top: '80%', left: '52%', dur: '16s', delay: '-2s', size: 46 },
  { n: '90', top: '8%', left: '88%', dur: '13s', delay: '-9s', size: 38 },
  { n: '33', top: '58%', left: '93%', dur: '15s', delay: '-5s', size: 50 },
];

const HomeComercial = () => {
  const [navSolida, setNavSolida] = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [videoOk, setVideoOk] = useState(true);

  // Extractos reales desde Firestore.
  //
  // Acá NO hay datos de ejemplo de respaldo. Los había, y se mostraban en el
  // primer render mientras Firestore contestaba: durante ese rato la página
  // publicaba resultados y números de sorteo inventados como si fueran los
  // del día. En un sitio oficial de lotería eso no es un detalle estético.
  // Mientras carga se muestra un esqueleto, y si no hay datos se dice.
  const { datos: extractos, cargando: cargandoExtractos } = useExtractos();
  const tickerItems = tickerDeExtractos(extractos);
  const hayExtractos = !!extractos?.quiniela?.length;
  const pageRef = useRef(null);
  const videoRef = useRef(null);

  // Video de fondo indetenible:
  // 1) El tamaño se congela en píxeles al montar (ningún reflow del hero
  //    puede redimensionarlo y disparar la pausa del navegador).
  // 2) Si igual se pausa, el evento `pause` lo reanuda al instante.
  // 3) Watchdog cada 700ms como última red (pausas y congelamientos).
  useEffect(() => {
    if (!videoOk) return undefined;
    const v = videoRef.current;
    if (!v) return undefined;

    const congelarTamano = () => {
      const hero = v.closest('.hc-hero');
      if (hero) {
        v.style.width = `${hero.offsetWidth}px`;
        v.style.height = `${hero.offsetHeight}px`;
      }
    };
    congelarTamano();
    window.addEventListener('resize', congelarTamano);

    const reanudar = () => {
      if (document.visibilityState !== 'hidden') v.play().catch(() => {});
    };
    v.addEventListener('pause', reanudar);
    v.addEventListener('stalled', reanudar);
    v.addEventListener('suspend', reanudar);

    let ultimoTiempo = -1;
    const id = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      if (v.paused || v.ended) {
        v.play().catch(() => {});
      } else if (v.currentTime === ultimoTiempo && !v.seeking && v.readyState >= 2) {
        v.currentTime = 0;
        v.play().catch(() => {});
      }
      ultimoTiempo = v.currentTime;
    }, 700);

    return () => {
      window.removeEventListener('resize', congelarTamano);
      v.removeEventListener('pause', reanudar);
      v.removeEventListener('stalled', reanudar);
      v.removeEventListener('suspend', reanudar);
      clearInterval(id);
    };
  }, [videoOk]);

  // Nav que se condensa al scrollear
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setNavSolida(window.scrollY > 24);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Reveals al scroll
  useEffect(() => {
    const nodos = pageRef.current?.querySelectorAll('.hc-reveal') ?? [];
    const reducido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducido || !('IntersectionObserver' in window)) {
      nodos.forEach((n) => n.classList.add('in'));
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    nodos.forEach((n) => io.observe(n));
    return () => io.disconnect();
    // Depende de `extractos` porque el tablero se monta recién cuando
    // contesta Firestore, después de este efecto. Sin esto, sus nodos nunca
    // entran al observer, nunca reciben la clase `in` y quedan en opacity:0:
    // la sección se ve vacía aunque el tablero esté renderizado.
  }, [extractos]);

  // Bloquear scroll del body con el menú móvil abierto
  useEffect(() => {
    document.body.style.overflow = menuAbierto ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuAbierto]);

  const cerrarMenu = () => setMenuAbierto(false);

  // ⚠️ La app monta con HashRouter, así que un href="#juegos" NO es un ancla: el router
  // lee el hash como la RUTA /juegos, no la encuentra, y deja al visitante en una página
  // en blanco con el navbar institucional viejo. Le pasaba a los 15 links de esta home.
  // Por eso el scroll se hace a mano y se cancela la navegación del ancla.
  // El href se conserva para que el link siga siendo un link de verdad: foco, teclado,
  // "abrir en pestaña nueva" y lectores de pantalla.
  const irASeccion = (e, href) => {
    // El preventDefault va PRIMERO y siempre, exista o no el destino: si un id se borra
    // o se escribe mal, el peor caso tiene que ser "no pasa nada", nunca volver a echar
    // al visitante fuera de la home.
    e.preventDefault();
    const destino = document.getElementById(String(href).replace('#', ''));
    if (!destino) return;
    cerrarMenu();
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    destino.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  };

  return (
    <div className="hc-page" ref={pageRef} id="inicio">
      <div className="hc-serrania" aria-hidden="true"></div>

      <header className={`hc-nav${navSolida || menuAbierto ? ' is-solid' : ''}${menuAbierto ? ' menu-open' : ''}`}>
        <div className="hc-nav-inner">
          <a href="#inicio" className="hc-logo" aria-label="INPROJUY Juega — inicio" onClick={(e) => irASeccion(e, '#inicio')}>
            <img src={LOGO_BLANCO} alt="INPROJUY" />
            <span className="hc-tag">Juega</span>
          </a>
          <nav className="hc-nav-links" aria-label="Navegación principal">
            {NAV_LINKS.map((l) => <a key={l.label} href={l.href} onClick={(e) => irASeccion(e, l.href)}>{l.label}</a>)}
          </nav>
          <div className="hc-nav-cta">
            <Link to="/login" className="hc-btn hc-btn-ghost">Ingresar</Link>
            <a href="#juegos" className="hc-btn hc-btn-coral" onClick={(e) => irASeccion(e, '#juegos')}>Jugar ahora</a>
          </div>
          <button
            className="hc-burger"
            aria-label={menuAbierto ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuAbierto}
            onClick={() => setMenuAbierto((v) => !v)}
          >
            <span></span><span></span><span></span>
          </button>
        </div>
      </header>

      <div className={`hc-mobile-menu${menuAbierto ? ' open' : ''}`}>
        {NAV_LINKS.map((l) => <a key={l.label} href={l.href} onClick={(e) => irASeccion(e, l.href)}>{l.label}</a>)}
        <div className="hc-mm-cta">
          <Link to="/login" className="hc-btn hc-btn-ghost" onClick={cerrarMenu}>Ingresar</Link>
          <a href="#juegos" className="hc-btn hc-btn-coral" onClick={(e) => irASeccion(e, '#juegos')}>Jugar ahora</a>
        </div>
      </div>

      <section className="hc-hero">
        {/* Video de fondo opcional: poné el mp4 en public/video/hero-bg.mp4 y aparece solo */}
        {videoOk && (
          <>
            <video
              ref={videoRef}
              className="hc-hero-video"
              autoPlay muted loop playsInline
              preload="auto"
              onError={() => setVideoOk(false)}
              src={`${PUB}/video/hero-bg.mp4`}
            />
            <div className="hc-hero-velo" aria-hidden="true"></div>
          </>
        )}
        <div className="hc-bolas-flotantes" aria-hidden="true">
          {BOLAS_FLOTANTES.map((b) => (
            <span
              key={b.n}
              className="hc-bola-flotante"
              style={{
                top: b.top, left: b.left,
                width: b.size, height: b.size, fontSize: b.size * 0.38,
                animationDuration: b.dur, animationDelay: b.delay,
              }}
            >
              {b.n}
            </span>
          ))}
        </div>
        <div className="hc-wrap hc-hero-grid">
          {/* P1 · La columna izquierda se parte en dos: el eyebrow por un
              lado y el resto del texto por otro.

              En escritorio esto no cambia nada — `.hc-hero-col` es un div
              común y la grilla ve los mismos dos hijos de siempre.

              En móvil (≤860px) `.hc-hero-col` pasa a `display:contents` y
              sus dos hijos suben a ser ítems de la grilla, así que el
              carrusel se puede meter ENTRE el eyebrow y el titular con
              `order`. Sin esta partición no hay forma de intercalarlo: el
              eyebrow y el titular serían un solo bloque indivisible. */}
          <div className="hc-hero-col">
            <span className="hc-eyebrow hc-enter">Juegos oficiales de la Provincia de Jujuy</span>
            <div className="hc-hero-texto">
              <h1 className="hc-enter d1">Jugá, mirá los extractos y hacete <span className="hc-gold">millonario</span></h1>
              <p className="hc-hero-sub hc-enter d2">
                Resultados al instante, juegos regulados por INPROJUY y agencias oficiales
                en toda la provincia. Todo lo que necesitás, en un solo lugar.
              </p>
              <div className="hc-hero-ctas hc-enter d3">
                <a href="#resultados" className="hc-btn hc-btn-gold" onClick={(e) => irASeccion(e, '#resultados')}>Ver resultados de hoy</a>
                <a href="#juegos" className="hc-btn hc-btn-ghost" onClick={(e) => irASeccion(e, '#juegos')}>Cómo se juega</a>
              </div>
              <div className="hc-trust-row hc-enter d4">
                <div className="hc-trust-item"><strong>+120</strong><span>agencias oficiales</span></div>
                {/* El número sale de la lista, no a mano: decía 7 y la grilla
                    tiene 8 juegos. Un dato falso a la vista en el hero. */}
                <div className="hc-trust-item"><strong>{JUEGOS.length}</strong><span>juegos oficiales</span></div>
                <div className="hc-trust-item"><strong>Todos los días</strong><span>resultados publicados</span></div>
              </div>
            </div>
          </div>

          {/* Acá había una tarjeta fija de Loto Plus con el pozo, el número de
              sorteo, la fecha del próximo sorteo y las bolillas INVENTADOS —y
              la fecha ya había pasado hacía dos semanas—. El carrusel muestra
              los juegos que hoy tienen dato real en Firestore, y ninguno más. */}
          <CarruselJuegos
            extractos={extractos}
            cargandoExtractos={cargandoExtractos}
            irASeccion={irASeccion}
          />
        </div>
      </section>

      <div className="hc-serrania" aria-hidden="true"></div>

      {/* La marquesina solo existe si hay resultados de verdad que pasar.
          Antes caía a una lista de ejemplo y hacía desfilar números de
          sorteo inventados por toda la home. */}
      {tickerItems?.length ? (
        <div className="hc-ticker" aria-hidden="true">
          <div className="hc-ticker-track">
            {[...tickerItems, ...tickerItems].map((t, i) => (
              <span className="hc-ticker-item" key={i}>{t}<em>◆</em></span>
            ))}
          </div>
        </div>
      ) : null}

      <section className="hc-section hc-results" id="resultados">
        <div className="hc-wrap">
          <div className="hc-sec-head hc-reveal">
            <div>
              <p className="hc-sec-kicker">Últimos resultados</p>
              <h2>Extractos de Tómbola</h2>
            </div>
            <p>Resultados oficiales publicados por Lotería Jujeña apenas termina cada sorteo.</p>
          </div>

          {hayExtractos ? (
            <TableroExtractos datos={extractos} />
          ) : (
            // Sin números mientras no haya datos confirmados: primero el
            // esqueleto, y si terminó de cargar y no llegó nada, se avisa.
            // Este bloque tiene que existir en los tres estados (cargando, con
            // datos y sin datos): la sección #resultados es destino del menú.
            <div className="hc-board hc-reveal dly-1">
              <div className="hc-board-bar">
                <h3>Sorteos del día</h3>
                {cargandoExtractos && <span className="hc-live-dot">Cargando</span>}
              </div>

              {cargandoExtractos ? (
                <div className="hc-board-cargando" aria-busy="true" aria-live="polite">
                  <span className="hc-sr-only">Buscando los extractos del día</span>
                  <span className="hc-esqueleto hc-esq-logo" />
                  <span className="hc-esqueleto hc-esq-linea" />
                  <span className="hc-esqueleto hc-esq-numero" />
                  <span className="hc-esqueleto hc-esq-boton" />
                </div>
              ) : (
                <div className="hc-board-sindatos">
                  <p>Todavía no pudimos cargar los extractos de hoy.</p>
                  <p>Los resultados oficiales están en loterialajujeña.com.ar</p>
                </div>
              )}

              <div className="hc-board-foot">
                <a href="#resultados" onClick={(e) => irASeccion(e, '#resultados')}>Ver todos los extractos <span aria-hidden="true">→</span></a>
              </div>
            </div>
          )}

          {/* El extracto de cada juego, con el vestido de su juego. Va acá,
              debajo del tablero de la quiniela y dentro de la misma sección:
              el visitante que vino a chequear un número ya encontró su turno
              arriba, y lo que sigue son los juegos de pozo grande, que es lo
              que vende. Es un componente aparte y no una rama del tablero
              porque no comparte ni la estructura ni la fuente: la quiniela son
              20 números por turno y por lotería; esto son N modalidades con
              sus premios y sus ganadores.

              Se le pasan los mismos `extractos` que ya pidió la home: el Pozo
              de la Quiniela sale de ahí, y volver a llamar a useExtractos()
              duplicaría dos lecturas de Firestore por visita. */}
          <ExtractosJuegos
            extractos={extractos}
            cargandoExtractos={cargandoExtractos}
          />

          {/* P8 · El armador de jugadas vive ACÁ, dentro del contenido, y ya no
              flotando en la esquina. Flotando tenía firma visual de aviso —y el
              visitante filtra los avisos antes de leerlos— y además tapaba en
              móvil la leyenda de juego responsable, que es obligatoria. */}
          <NumeroSuerte />
        </div>
      </section>

      <section className="hc-section" id="juegos">
        <div className="hc-wrap">
          <div className="hc-sec-head hc-reveal">
            <div>
              <p className="hc-sec-kicker">Juegos oficiales</p>
              <h2>Elegí tu juego</h2>
            </div>
            <a href="#juegos" className="hc-sec-link" onClick={(e) => irASeccion(e, '#juegos')}>
              Ver todas las modalidades <span aria-hidden="true">→</span>
            </a>
          </div>
          <div className="hc-games-grid">
            {JUEGOS.map((j) => (
              <article
                key={j.nombre}
                className={`hc-game-card ${j.clase}${j.destacada ? ' es-destacada' : ''} hc-reveal${j.delay ? ` ${j.delay}` : ''}`}
              >
                <div className="hc-gc-top">
                  <span className="hc-gc-freq">{j.freq}</span>
                  <img className="hc-gc-logo" src={j.logo} alt={j.nombre} loading="lazy" />
                </div>
                <div className="hc-gc-body">
                  <h3>{j.nombre}</h3>
                  {j.sub && <p className="hc-gc-sub">{j.sub}</p>}
                  {/* La línea que distingue a este juego de los otros siete.
                      Antes acá había ocho "Cómo se juega" idénticos apuntando
                      a un id inexistente. */}
                  <p className="hc-gc-dato">{j.dato}</p>
                  <ul className="hc-gc-chips">
                    {j.chips.map((c) => <li key={c}>{c}</li>)}
                  </ul>
                  {j.cta && (
                    <a
                      href={j.cta.href}
                      className="hc-gc-link"
                      onClick={(e) => irASeccion(e, j.cta.href)}
                    >
                      {j.cta.label} <span aria-hidden="true">→</span>
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="hc-serrania" aria-hidden="true"></div>

      <footer className="hc-footer">
        <div className="hc-wrap">
          <div className="hc-footer-grid">
            <div className="hc-footer-brand">
              <a href="#inicio" className="hc-logo" aria-label="INPROJUY Juega — inicio" onClick={(e) => irASeccion(e, '#inicio')}>
                <img src={LOGO_BLANCO} alt="INPROJUY" />
              </a>
              <p>Instituto Provincial de Juegos de Azar de Jujuy. Juegos regulados, resultados oficiales de Lotería Jujeña.</p>
            </div>
            <div className="hc-footer-links">
              <div>
                <h4>Jugá con confianza</h4>
                <Link to="/juegoResponsable">Juego responsable</Link>
                <Link to="/formulario">Autoexclusión</Link>
              </div>
              <div>
                <h4>Accesos</h4>
                <Link to="/agencias">Agencias</Link>
                <Link to="/login">Ingresar</Link>
              </div>
            </div>
            <div className="hc-pill-18">
              <span className="hc-badge">18+</span>
              Prohibido para menores de 18 años
            </div>
          </div>
          {/* P3 · Acá decía "Jugar compulsivamente es perjudicial. Línea de
              ayuda: 0800-333-0333", en crema al 40% (3,59:1, por debajo de
              AA) y al mismo peso que el copyright. No era la fórmula que
              exige la norma ni tenía el "+18" al lado.
              El texto está en `LeyendaJuegoResponsable.jsx` — un solo lugar
              para cambiarlo el día que el área legal confirme si a Jujuy le
              aplica el régimen nacional o una norma provincial. */}
          <LeyendaJuegoResponsable variante="pie" />

          <div className="hc-footer-bottom">
            <small>© 2026 INPROJUY · Provincia de Jujuy, Argentina</small>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomeComercial;
